import * as React from 'react'
import styled from 'styled-components'
import * as constants from '../../../static/constants'
import PartnerVenueHeader from 'components/organisms/PartnerVenueHeader'

interface IGuest {
  seller: string
  seller_booking_id: string | null
  guest_name: string
  adults: number
  children: number
  infants: number
  memo: string | null
}

interface ITour {
  tour_id: number
  name: string
  start_time: string
  guests: IGuest[]
}

interface IDay {
  date: string
  tours: ITour[]
}

interface IProps {
  venue_name: string
  slug: string
  guest_list_active: boolean
  month: string
  today: string
  month_total: { adults: number; children: number; infants: number; people: number }
  days: IDay[]
}

const GuestList: React.FC<IProps> = props => {
  const { slug, today, month_total, days } = props
  const monthPath = `/partner_venues/${slug}/guest_list`

  return (
    <Wrapper>
      <PartnerVenueHeader
        {...props}
        active="guest_list"
        monthPath={monthPath}
        summary={
          <>
            合計<strong>{month_total.people}</strong>人
            <small>
              （大人{month_total.adults}・子供{month_total.children}・幼児{month_total.infants}）
            </small>
          </>
        }
      />
      <main className="guestList">
        {days.length === 0 ? (
          <p className="empty">この月のゲスト名簿はありません。</p>
        ) : (
          days.map(day => (
            <section className="day" key={day.date}>
              <h2 className={day.date === today ? 'today' : ''}>
                {day.date === today && <span>今日</span>}
                {day.date.replace(/-/g, '/')}
              </h2>
              <div className="dayTours">
                {day.tours.map(tour => (
                  <section className="tour" key={tour.tour_id}>
                    <h3>
                      {tour.name} <span>{tour.start_time}</span>
                    </h3>
                    <div className="tableContainer">
                      <table>
                        <colgroup>
                          <col className="sellerColumn" />
                          <col className="sellerBookingIdColumn" />
                          <col className="guestNameColumn" />
                          <col className="peopleColumn" />
                          <col className="memoColumn" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th>申込</th>
                            <th>予約番号</th>
                            <th>ゲスト名</th>
                            <th>人数</th>
                            <th>メモ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tour.guests.map((guest, index) => (
                            <tr key={`${guest.seller_booking_id || guest.guest_name}-${index}`}>
                              <td>{guest.seller}</td>
                              <td>{guest.seller_booking_id || '-'}</td>
                              <td>{guest.guest_name}</td>
                              <td className="people">
                                {guest.adults + guest.children + guest.infants}人
                                <small>
                                  （大{guest.adults}・子{guest.children}・幼{guest.infants}）
                                </small>
                              </td>
                              <td className="memo">{guest.memo || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ))}
              </div>
            </section>
          ))
        )}
      </main>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  min-height: 100vh;
  box-sizing: border-box;
  color: ${constants.COLORS.black};
  font-size: 13px;
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
    env(safe-area-inset-left);

  .guestList {
    box-sizing: border-box;
    width: 100%;
    margin: auto;
    padding: 16px;
  }
  .empty {
    color: ${constants.COLORS.subText};
    text-align: center;
    padding: 48px 0;
  }
  .day {
    margin-bottom: 28px;
  }
  .day > h2 {
    margin: 0 0 12px;
    font-size: 17px;
  }
  .day > h2.today {
    color: ${constants.COLORS.primary};
  }
  .day > h2 span {
    display: inline-block;
    margin-right: 6px;
    padding: 1px 5px;
    background: ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
    border-radius: 3px;
    font-size: 11px;
  }
  .dayTours {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
  .tour {
    box-sizing: border-box;
    min-width: 800px;
    border: 1px solid ${constants.COLORS.divider2};
    border-radius: 6px;
    overflow: hidden;
  }
  .tour + .tour {
    margin-top: 14px;
  }
  .tour h3 {
    box-sizing: border-box;
    margin: 0;
    padding: 10px 12px;
    background: ${constants.COLORS.background};
    font-size: 14px;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  .sellerColumn {
    width: 13%;
  }
  .sellerBookingIdColumn {
    width: 17%;
  }
  .guestNameColumn {
    width: 20%;
  }
  .peopleColumn {
    width: 18%;
  }
  .memoColumn {
    width: 32%;
  }
  th,
  td {
    padding: 10px 12px;
    border-top: 1px solid ${constants.COLORS.divider2};
    text-align: left;
    vertical-align: top;
  }
  th {
    color: ${constants.COLORS.subText};
    background: ${constants.COLORS.background};
    font-size: 12px;
  }
  td small {
    display: inline;
    margin-left: 4px;
    color: ${constants.COLORS.subText};
    font-size: 11px;
    white-space: nowrap;
  }
  .people {
    white-space: nowrap;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      white-space: normal;
    }
  }
  .memo {
    min-width: 180px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
    font-size: 14px;
    .guestList {
      padding: 24px;
    }
    .tour {
      min-width: 700px;
    }
  }
`

export default GuestList
